import React, { useState, useEffect, useMemo } from 'react';
import { Cat, User, FilterState } from '../types';
import { getCats, saveCat, deleteCat, getUniqueValues, exportDatabase } from '../services/db';
import { CatForm } from './CatForm';
import { CatDetail } from './CatDetail';
import { UserManagement } from './UserManagement';
import { SponsorManagement } from './SponsorManagement';
import { 
  LogOut, Plus, Search, LayoutGrid, List as ListIcon, 
  Syringe, Heart, Sparkles, Skull, Users, Loader2,
  Trash2, Pencil, Download
} from 'lucide-react';

interface CatManagerProps {
  user: User;
  onLogout: () => void;
}

export const CatManager: React.FC<CatManagerProps> = ({ user, onLogout }) => {
  const [cats, setCats] = useState<Cat[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUserMgmtOpen, setIsUserMgmtOpen] = useState(false);
  const [isSponsorMgmtOpen, setIsSponsorMgmtOpen] = useState(false);
  
  // States for Editing/Viewing
  const [editingCat, setEditingCat] = useState<Cat | null>(null);
  const [viewingCat, setViewingCat] = useState<Cat | null>(null);
  
  // Specific state to track which ID is currently being deleted
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [dropdownOptions, setDropdownOptions] = useState({ names: [] as string[], vaccinations: [] as string[], godparents: [] as string[] });

  // Sort State
  const [sortBy, setSortBy] = useState<'name' | 'admissionDate' | 'nextVaccination' | 'none'>('none');

  // Filter State
  const [filters, setFilters] = useState<FilterState>({
    name: '',
    isChipped: 'all',
    gender: 'all',
    terrain: 'all',
    isNeutered: 'all',
    vaccination: '',
    godparent: '',
  });

  // Load Data
  const refreshData = async () => {
    // Only set global loading on first load to prevent flickering
    if (cats.length === 0) setLoading(true);
    try {
        const data = await getCats();
        const options = await getUniqueValues();
        setCats(data);
        setDropdownOptions(options);
    } catch (e) {
        console.error("Error refreshing data:", e);
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  // Filter and Sort Logic
  const filteredCats = useMemo(() => {
    let filtered = cats.filter(cat => {
      if (filters.name && cat.name !== filters.name) return false;
      if (filters.isChipped !== 'all' && !!cat.chip !== (filters.isChipped === 'yes')) return false;
      if (filters.gender !== 'all' && cat.gender !== filters.gender) return false;
      if (filters.terrain !== 'all' && cat.terrain !== filters.terrain) return false;
      if (filters.isNeutered !== 'all' && cat.isNeutered !== (filters.isNeutered === 'yes')) return false;
      if (filters.vaccination && !(cat.vaccinations || []).some(v => v.name === filters.vaccination)) return false;
      if (filters.godparent && cat.godparent !== filters.godparent) return false;
      return true;
    });

    // Apply sorting
    if (sortBy === 'name') {
      filtered = filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'admissionDate') {
      filtered = filtered.sort((a, b) => new Date(b.admissionDate).getTime() - new Date(a.admissionDate).getTime());
    } else if (sortBy === 'nextVaccination') {
      filtered = filtered.sort((a, b) => {
        const aNext = a.nextVaccination?.date;
        const bNext = b.nextVaccination?.date;
        
        if (!aNext && !bNext) return 0;
        if (!aNext) return 1;
        if (!bNext) return -1;
        
        return new Date(aNext).getTime() - new Date(bNext).getTime();
      });
    }

    return filtered;
  }, [cats, filters, sortBy]);

  const handleSave = async (cat: Cat) => {
    await saveCat(cat);
    await refreshData();
    setIsModalOpen(false);
    setEditingCat(null);
  };

  // --- REWRITTEN DELETE LOGIC ---
  // Returns TRUE if deleted, FALSE if cancelled or failed
  const handleDelete = async (id: string, e?: React.MouseEvent): Promise<boolean> => {
    // STOP EVERYTHING: Stop the click from bubbling up to parents
    if (e) {
        e.preventDefault();
        e.stopPropagation();
    }

    console.log("Button clicked for ID:", id);

    if (!id) {
        alert("Error: El registro no tiene ID.");
        return false;
    }

    if (!window.confirm('¿Eliminar entrada irreversiblemente?')) {
        return false;
    }

    // Visual Feedback: Show spinner on this specific item
    setDeletingId(id);

    try {
        // 1. Database Call
        await deleteCat(id);
        
        // 2. Local Update (Optimistic)
        setCats(prev => prev.filter(c => c.id !== id));
        
        // 3. Close details if open
        if (viewingCat?.id === id) setViewingCat(null);
        
        // 4. FORCE REFRESH from DB to ensure sync
        await refreshData();
        
        return true;

    } catch (err: any) {
        console.error("Delete Error:", err);
        alert("Error al eliminar. ¿Tiene permisos de escritura en Firestore? " + err.message);
        // Refresh to ensure state is consistent
        refreshData();
        return false;
    } finally {
        // Reset loading state
        setDeletingId(null);
    }
  };

  const openEdit = (cat: Cat, e?: React.MouseEvent) => {
    if (e) {
        e.stopPropagation();
        e.preventDefault();
    }
    setEditingCat(cat);
    setIsModalOpen(true);
  };
  
  const openView = (cat: Cat) => {
      setViewingCat(cat);
  };

  return (
    <div className="flex flex-col min-h-screen w-full overflow-hidden">
      {/* Top Header */}
      <header className="bg-white/80 backdrop-blur-md sticky top-0 z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl sm:text-2xl font-black bg-gradient-to-r from-yellow-400 via-red-500 to-purple-600 bg-clip-text text-transparent truncate mr-2">
              PurrfectManager
            </h1>
            <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
              <span className="hidden md:block text-sm font-medium text-gray-600">
                {user.name} ({user.role})
              </span>
              
              <button
                onClick={() => setIsSponsorMgmtOpen(true)}
                className="p-2 rounded-full text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                title="Padrinos"
              >
                <Heart className="h-5 w-5" />
              </button>

              <button
                onClick={() => setIsUserMgmtOpen(true)}
                className={`p-2 rounded-full transition-colors ${user.role === 'admin' ? 'text-purple-600 bg-purple-50 hover:bg-purple-100' : 'text-gray-600 hover:bg-gray-100'}`}
                title="Equipo"
              >
                <Users className="h-5 w-5" />
              </button>

              {user.role === 'admin' && (
                <button
                  onClick={exportDatabase}
                  className="p-2 rounded-full text-green-600 bg-green-50 hover:bg-green-100 transition-colors"
                  title="Exportar base de datos"
                >
                  <Download className="h-5 w-5" />
                </button>
              )}

              <button 
                onClick={onLogout}
                className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
                title="Cerrar sesión"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="py-2 sm:py-4 space-y-3 md:space-y-0 md:flex md:items-center md:gap-4 overflow-x-auto pb-4 no-scrollbar">
            {/* Name Filter */}
            <div className="relative min-w-[140px]">
              <select 
                className="w-full pl-3 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-purple-500 focus:border-purple-500 appearance-none"
                value={filters.name}
                onChange={(e) => setFilters({...filters, name: e.target.value})}
              >
                <option value="">Todos los nombres</option>
                {dropdownOptions.names.map(name => <option key={name} value={name}>{name}</option>)}
              </select>
              <Search className="absolute right-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

             {/* Filter: Pate */}
             <div className="relative min-w-[140px]">
              <select 
                className="w-full pl-3 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-purple-500 focus:border-purple-500 appearance-none"
                value={filters.godparent}
                onChange={(e) => setFilters({...filters, godparent: e.target.value})}
              >
                <option value="">Todos los padrinos</option>
                {dropdownOptions.godparents.map(gp => <option key={gp} value={gp}>{gp}</option>)}
              </select>
              <Heart className="absolute right-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

             {/* Filter: Impfung */}
             <div className="relative min-w-[140px]">
              <select 
                className="w-full pl-3 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-purple-500 focus:border-purple-500 appearance-none"
                value={filters.vaccination}
                onChange={(e) => setFilters({...filters, vaccination: e.target.value})}
              >
                <option value="">Todas las vacunas</option>
                {dropdownOptions.vaccinations.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <Syringe className="absolute right-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Quick Toggles */}
            <div className="flex gap-2">
                <select 
                    className="pl-2 pr-6 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-purple-500"
                    value={filters.isChipped}
                    onChange={(e) => setFilters({...filters, isChipped: e.target.value as any})}
                >
                    <option value="all">Chip: Todos</option>
                    <option value="yes">Sí</option>
                    <option value="no">No</option>
                </select>

                <select 
                    className="pl-2 pr-6 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-purple-500"
                    value={filters.gender}
                    onChange={(e) => setFilters({...filters, gender: e.target.value as any})}
                >
                    <option value="all">Sexo: Todos</option>
                    <option value="male">M</option>
                    <option value="female">H</option>
                </select>

                <select 
                    className="pl-2 pr-6 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-purple-500"
                    value={filters.terrain}
                    onChange={(e) => {
                        const value = e.target.value;
                        const terrainValue = value === 'all' ? 'all' : (parseInt(value) === 1 ? 1 : 2);
                        setFilters({...filters, terrain: terrainValue});
                    }}
                >
                    <option value="all">Terreno: Todos</option>
                    <option value={1}>T1</option>
                    <option value={2}>T2</option>
                </select>

                 <select 
                    className="pl-2 pr-6 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-purple-500"
                    value={filters.isNeutered}
                    onChange={(e) => setFilters({...filters, isNeutered: e.target.value as any})}
                >
                    <option value="all">Castr: Todos</option>
                    <option value="yes">Sí</option>
                    <option value="no">No</option>
                </select>
            </div>

            {/* Sort Options */}
            <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-600 whitespace-nowrap">Ordenar:</span>
                <select 
                    className="pl-2 pr-6 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-purple-500"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                >
                    <option value="none">Sin ordenar</option>
                    <option value="name">Nombre</option>
                    <option value="admissionDate">Fecha de ingreso</option>
                    <option value="nextVaccination">Próxima vacuna</option>
                </select>
            </div>

             {/* View Toggle */}
            <div className="ml-auto flex bg-gray-100 rounded-lg p-1">
                <button 
                    onClick={() => setViewMode('card')}
                    className={`p-1.5 rounded-md transition-all ${viewMode === 'card' ? 'bg-white shadow-sm text-purple-600' : 'text-gray-400'}`}
                >
                    <LayoutGrid className="h-5 w-5" />
                </button>
                <button 
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-purple-600' : 'text-gray-400'}`}
                >
                    <ListIcon className="h-5 w-5" />
                </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8 w-full">
        {loading ? (
             <div className="flex justify-center items-center h-64">
                <Loader2 className="h-12 w-12 text-purple-500 animate-spin" />
             </div>
        ) : filteredCats.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 animate-[fadeIn_0.5s_ease-out]">
                <Search className="h-12 w-12 mb-4 opacity-20" />
                <p>No se encontraron gatos.</p>
            </div>
        ) : (
            viewMode === 'card' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-[slideUp_0.5s_ease-out]">
                    {filteredCats.map(cat => (
                        <CatCard 
                            key={cat.id} 
                            cat={cat} 
                            onEdit={openEdit} 
                            onDelete={handleDelete} 
                            onView={openView}
                            isDeleting={deletingId === cat.id}
                        />
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 animate-[slideUp_0.5s_ease-out] overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Padrino</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Vacunas</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredCats.map(cat => (
                                <CatRow 
                                    key={cat.id} 
                                    cat={cat} 
                                    onEdit={openEdit} 
                                    onDelete={handleDelete} 
                                    onView={openView}
                                    isDeleting={deletingId === cat.id}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )
        )}
      </main>

      {/* FAB for Adding */}
      <button
        onClick={() => { setEditingCat(null); setIsModalOpen(true); }}
        className="fixed bottom-6 right-6 p-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all z-30"
      >
        <Plus className="h-7 w-7" />
      </button>

      {/* Detail Overlay */}
      {viewingCat && (
          <CatDetail cat={viewingCat} onClose={() => setViewingCat(null)} />
      )}

      {/* Edit/Add Modal */}
      <CatForm 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialData={editingCat}
        onSave={handleSave}
        onDelete={(id) => handleDelete(id)}
        existingVaccinations={dropdownOptions.vaccinations}
        existingGodparents={dropdownOptions.godparents}
      />

      {/* User Management Modal */}
      {isUserMgmtOpen && (
          <UserManagement 
            currentUser={user} 
            onClose={() => setIsUserMgmtOpen(false)} 
          />
      )}

      {/* Sponsor Management Modal */}
      {isSponsorMgmtOpen && (
          <SponsorManagement 
            onClose={() => setIsSponsorMgmtOpen(false)} 
            onChanged={refreshData}
          />
      )}
    </div>
  );
};

// --- ISOLATED COMPONENTS ---

const CatCard: React.FC<{ 
    cat: Cat; 
    onEdit: (c: Cat, e: React.MouseEvent) => void; 
    onDelete: (id: string, e: React.MouseEvent) => void; 
    onView: (c: Cat) => void;
    isDeleting: boolean;
}> = ({ cat, onEdit, onDelete, onView, isDeleting }) => {
    
    const mainPhoto = cat.photos && cat.photos.length > 0 ? cat.photos[0] : `https://ui-avatars.com/api/?name=${cat.name}&background=random`;
    const isDead = !!cat.deathDate;

    return (
        <div 
            onClick={() => !isDeleting && onView(cat)} // Disable click if deleting
            className={`group bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col h-full cursor-pointer ${isDead ? 'grayscale opacity-80' : ''} ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`}
        >
            <div className="aspect-[4/3] w-full overflow-hidden bg-gray-100 relative">
                <img src={mainPhoto} alt={cat.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute top-2 right-2 flex gap-1">
                    {cat.gender === 'male' ? 
                        <span className="bg-blue-100 text-blue-700 p-1 rounded-md text-xs font-bold">♂</span> : 
                        <span className="bg-pink-100 text-pink-700 p-1 rounded-md text-xs font-bold">♀</span>
                    }
                    <span className="bg-indigo-100 text-indigo-700 p-1 rounded-md text-xs font-bold">T{cat.terrain}</span>
                </div>
            </div>
            
            <div className="p-5 pb-2 flex-1">
                <div className="flex justify-between items-start mb-2 gap-2">
                    <h3 className="text-xl font-bold text-gray-800 truncate" title={cat.name}>{cat.name}</h3>
                    <div className="flex gap-1 text-xs flex-shrink-0">
                        {cat.chip && <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">Chip</span>}
                    </div>
                </div>
                <div className="text-sm text-gray-600 flex items-center gap-2">
                    <Heart className="h-4 w-4 text-rose-400" />
                    <span className="truncate">{cat.godparent || 'Sin padrino'}</span>
                </div>
            </div>

            {/* Actions Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between mt-auto">
                <div className="flex gap-3">
                    <button 
                        onClick={(e) => onEdit(cat, e)} 
                        className="h-10 w-10 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors"
                        title="Editar"
                    >
                        <Pencil className="h-5 w-5" />
                    </button>
                    <button 
                        onClick={(e) => onDelete(cat.id, e)} 
                        disabled={isDeleting}
                        className="h-10 w-10 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors disabled:opacity-50"
                        title="Eliminar"
                    >
                        {isDeleting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
                    </button>
                </div>
            </div>
        </div>
    )
}

const CatRow: React.FC<{ 
    cat: Cat; 
    onEdit: (c: Cat, e: React.MouseEvent) => void; 
    onDelete: (id: string, e: React.MouseEvent) => void; 
    onView: (c: Cat) => void;
    isDeleting: boolean;
}> = ({ cat, onEdit, onDelete, onView, isDeleting }) => {

    // Helper for rendering clickable cells
    const Cell = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
        <td 
            onClick={() => !isDeleting && onView(cat)} 
            className={`px-6 py-4 whitespace-nowrap cursor-pointer ${className} ${isDeleting ? 'opacity-50' : ''}`}
        >
            {children}
        </td>
    );

    return (
        <tr className={`hover:bg-gray-50 transition-colors ${isDeleting ? 'bg-red-50' : ''}`}>
            
            <Cell>
                <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                        <img className="h-10 w-10 rounded-full object-cover" src={cat.photos?.[0] || `https://ui-avatars.com/api/?name=${cat.name}`} alt="" />
                    </div>
                    <div className="ml-4 overflow-hidden">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[110px] sm:max-w-xs" title={cat.name}>{cat.name}</div>
                        <div className="text-sm text-gray-500">{cat.gender === 'male' ? 'Masculino' : 'Femenino'} • T{cat.terrain}</div>
                    </div>
                </div>
            </Cell>

            <Cell>
                <div className="flex flex-col gap-1">
                    {cat.chip ? 
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 w-fit">Chipado</span> :
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 w-fit">No chipado</span>
                    }
                    {cat.isNeutered && <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800 w-fit">Castrado</span>}
                    {cat.deathDate && <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-black text-white w-fit">Fallecido</span>}
                 </div>
            </Cell>

            <Cell className="hidden md:table-cell">
                <div className="text-sm text-gray-900">{cat.godparent || '-'}</div>
            </Cell>

            <Cell className="hidden sm:table-cell">
                 <div className="flex flex-wrap gap-1">
                    {(cat.vaccinations || []).slice(0, 3).map(v => (
                         <span key={v.id} className="px-2 py-0.5 text-xs rounded bg-blue-50 text-blue-700 border border-blue-100">{v.name}</span>
                    ))}
                    {(cat.vaccinations || []).length > 3 && <span className="text-xs text-gray-500">+{cat.vaccinations.length - 3}</span>}
                </div>
            </Cell>

            {/* ACTION COLUMN - COMPLETELY SEPARATE CLICK HANDLING */}
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex justify-end gap-3" onClick={(e) => e.stopPropagation()}>
                    <button 
                        type="button"
                        onClick={(e) => onEdit(cat, e)} 
                        className="text-purple-600 hover:text-purple-900 transition-colors p-2 bg-purple-50 rounded hover:bg-purple-100"
                        title="Editar"
                    >
                        <Pencil className="h-4 w-4" />
                    </button>
                    
                    <button 
                        type="button"
                        onClick={(e) => onDelete(cat.id, e)} 
                        disabled={isDeleting}
                        className={`text-red-600 hover:text-red-900 transition-colors p-2 bg-red-50 rounded hover:bg-red-100 flex items-center justify-center w-8 h-8 ${isDeleting ? 'cursor-not-allowed opacity-70' : ''}`}
                        title="Eliminar"
                    >
                        {isDeleting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Trash2 className="h-4 w-4" />
                        )}
                    </button>
                </div>
            </td>
        </tr>
    )
}